import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { Checkmark } from '@carbon/react/icons';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { fmtAmount, fmtNumber } from '../format';
import { sharedLineItemColumns } from './line-item-columns';
import { useRoyaltyCheckLineItemsSearchAtom } from '../show/atoms';
import type {
  RoyaltyCheckLineItem,
  RoyaltyCheckLineItemsSearchParams,
} from '../show/types';
import { columnFiltersToParams, LINE_ITEM_SORT_FIELD_MAP } from '../show/utils';

export type LineItemRow = RoyaltyCheckLineItem & Record<string, unknown>;

const shared = sharedLineItemColumns<LineItemRow>();

export const royaltyCheckLineItemColumns: DataTableColumnDef<LineItemRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Line Item Name',
  },
  {
    id: 'wellName',
    accessorKey: 'wellName',
    header: 'Well Name',
    cell: ({ row }) => {
      const { wellName, uwi, well } = row.original;
      if (!wellName) return '--';
      const href = well?.href ?? (uwi ? `/wells/${uwi}` : null);
      return href ? <Link to={href}>{wellName}</Link> : wellName;
    },
  },
  shared.productionMonth,
  {
    id: 'product',
    accessorKey: 'product',
    header: 'Product',
    cell: ({ row }) => row.original.product ?? '--',
  },
  {
    id: 'price',
    accessorKey: 'price',
    header: 'Price',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.price),
  },
  shared.interestType,
  shared.lineItemDecimal,
  shared.grossVolume,
  shared.grossValue,
  {
    id: 'grossTaxes',
    accessorKey: 'grossTaxes',
    header: 'Gross Taxes',
    meta: { type: 'decimal', align: 'right' },
    enableColumnFilter: false,
    cell: ({ row }) => fmtAmount(row.original.grossTaxes),
  },
  shared.grossAdjustment,
  {
    id: 'grossNet',
    accessorKey: 'grossNet',
    header: 'Gross Net',
    meta: { type: 'decimal', align: 'right' },
    enableColumnFilter: false,
    cell: ({ row }) => fmtAmount(row.original.grossNet),
  },
  {
    id: 'ownerVolume',
    accessorKey: 'ownerVolume',
    header: 'Owner Volume',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtNumber(row.original.ownerVolume),
  },
  {
    id: 'ownerValue',
    accessorKey: 'ownerValue',
    header: 'Owner Value',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.ownerValue),
  },
  {
    id: 'ownerTaxes',
    accessorKey: 'ownerTaxes',
    header: 'Owner Taxes',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.ownerTaxes),
  },
  {
    id: 'ownerAdjustment',
    accessorKey: 'ownerAdjustment',
    header: 'Owner Adjustment',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.ownerAdjustment),
  },
  {
    id: 'adjustmentCode',
    accessorKey: 'adjustmentCode',
    header: 'Adjustment Code',
    enableColumnFilter: false,
    cell: ({ row }) => row.original.adjustmentCode ?? '--',
  },
  {
    id: 'revenueAllocated',
    accessorKey: 'revenueAllocated',
    header: 'Revenue Allocated',
    enableColumnFilter: false,
    cell: ({ row }) => (row.original.revenueAllocated ? <Checkmark /> : null),
  },
];

interface RoyaltyCheckLineItemsTableProps {
  royaltyCheckId: string;
  persistenceKey: string;
  initialPageSize: number;
  enableColumnFilters?: boolean;
  className?: string;
  renderHeading?: (totalCount: number) => ReactNode;
  renderEmpty?: ReactNode;
  searchPlaceholder?: string;
}

export const RoyaltyCheckLineItemsTable: FC<
  RoyaltyCheckLineItemsTableProps
> = ({
  royaltyCheckId,
  persistenceKey,
  initialPageSize,
  enableColumnFilters = false,
  className,
  renderHeading,
  renderEmpty,
  searchPlaceholder = 'Search Line Items',
}) => {
  const { searchAtom } = useRoyaltyCheckLineItemsSearchAtom(royaltyCheckId);
  const [result, setPayload] = useAtom(searchAtom);
  const [params, setParams] = useState<RoyaltyCheckLineItemsSearchParams>({
    page: 1,
    pageSize: initialPageSize,
  });

  useEffect(() => {
    setPayload(() => params);
  }, [params, setPayload]);

  const refetch = useCallback((state: TableState) => {
    const sort = state.sorting[0];
    setParams((curr) => ({
      ...curr,
      ...columnFiltersToParams(state.columnFilters),
      page: state.page,
      pageSize: state.pageSize,
      sortAttribute: sort ? LINE_ITEM_SORT_FIELD_MAP[sort.id] : undefined,
      sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
      text: state.globalFilter || undefined,
    }));
  }, []);

  // Guard against the no-op refetch the table emits when search text is unchanged.
  const handleSearchChange = useCallback(
    (state: TableState) => {
      const newText = state.globalFilter || undefined;
      if (params.text === newText) return;
      refetch(state);
    },
    [params.text, refetch]
  );

  const rows = (result.data?.results ?? []) as LineItemRow[];
  const totalCount = result.data?.totalCount ?? 0;

  return (
    <div className={className}>
      {renderHeading?.(totalCount)}
      {rows.length === 0 && !result.loading && renderEmpty ? (
        renderEmpty
      ) : (
        <DataTable<LineItemRow>
          data={rows}
          columns={royaltyCheckLineItemColumns}
          getRowId={(row) => row.id}
          persistenceKey={persistenceKey}
          initialTableState={{ page: 1, pageSize: initialPageSize }}
          config={{
            globalSearch: true,
            sorting: true,
            columnFilters: enableColumnFilters,
            columnReordering: false,
            export: false,
          }}
          isLoading={result.loading}
          error={result.error ?? null}
          scroll={{
            mode: 'paginated',
            serverDriven: true,
            totalCount,
          }}
          onPaginationChange={refetch}
          onSortChange={refetch}
          onFilterChange={enableColumnFilters ? refetch : undefined}
          onGlobalSearchChange={handleSearchChange}
          searchPlaceholder={searchPlaceholder}
        />
      )}
    </div>
  );
};
